import { createSlice } from "@reduxjs/toolkit"
import {
  addContentLink,
  addContentPost,
  deleteContentLink,
  deleteContentPost,
  fetchContentLinks,
  fetchContentPosts,
  updateContentPost,
} from "./thunks"

/** @typedef {import("./types").ContentPost} ContentPost */
/** @typedef {import("./types").ContentLink} ContentLink */
/** @typedef {import("./types").ContentPageState} ContentPageState */
/** @typedef {import("@reduxjs/toolkit").PayloadAction} PayloadAction */

/** @type {ContentPageState} */
const initialState = {
  loadingPosts: false,
  newPostModalOpen: false,
  newLinkModalOpen: false,
  selectedPost: null,
  posts: [],

  loadingLinks: false,
  links: [],
}

const contentPageSlice = createSlice({
  name: "contentPage",
  initialState,
  reducers: {
    clearContentPageState: (_state, _action) => {
      return initialState
    },
    /**
     * @param {PayloadAction<ContentPost>} action
     */
    openPostModal: (state, action) => {
      state.selectedPost = action.payload || null
      state.newPostModalOpen = true
    },
    closePostModal: (state) => {
      state.newPostModalOpen = false
      state.selectedPost = null
    },
    openLinkModal: (state) => {
      state.newLinkModalOpen = true
    },
    closeLinkModal: (state) => {
      state.newLinkModalOpen = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContentPosts.pending, (state) => {
        state.loadingPosts = true
      })
      .addCase(fetchContentPosts.rejected, (state) => {
        state.loadingPosts = false
      })
      .addCase(fetchContentPosts.fulfilled, (state, action) => {
        state.posts = action.payload
        state.posts.sort((a, b) => a.id - b.id)

        state.loadingPosts = false
      })
      .addCase(fetchContentLinks.pending, (state) => {
        state.loadingLinks = true
      })
      .addCase(fetchContentLinks.rejected, (state, _action) => {
        state.loadingLinks = false
      })
      .addCase(fetchContentLinks.fulfilled, (state, action) => {
        state.links = action.payload
        state.loadingLinks = false
      })
      .addCase(deleteContentPost.pending, (state) => {
        state.loadingPosts = true
      })
      .addCase(deleteContentPost.rejected, (state) => {
        state.loadingPosts = false
      })
      .addCase(deleteContentPost.fulfilled, (state, action) => {
        const deletedId = action.payload
        state.posts = state.posts.filter((post) => post.contentPostId !== deletedId)
        state.posts.sort((a, b) => a.id - b.id)
        state.loadingPosts = false
      })
      .addCase(deleteContentLink.pending, (state) => {
        state.loadingLinks = true
      })
      .addCase(deleteContentLink.rejected, (state) => {
        state.loadingLinks = false
      })
      .addCase(deleteContentLink.fulfilled, (state, action) => {
        state.loadingLinks = false

        const deletedId = action.payload
        state.links = state.links.filter((link) => link.linkId !== deletedId)
        state.links.sort((a, b) => a.id - b.id)
      })
      .addCase(addContentPost.pending, (state) => {
        state.loadingPosts = true
      })
      .addCase(addContentPost.rejected, (state) => {
        state.loadingPosts = false
      })
      .addCase(addContentPost.fulfilled, (state, action) => {
        if (action.payload) {
          state.posts = [action.payload, ...state.posts]
        } else console.error("Expected action.payload to contain new post")
        state.loadingPosts = false
      })
      .addCase(addContentLink.pending, (state) => {
        state.loadingLinks = true
      })
      .addCase(addContentLink.rejected, (state) => {
        state.loadingLinks = false
      })
      .addCase(addContentLink.fulfilled, (state, action) => {
        if (action.payload) {
          state.links = [action.payload, ...state.links]
        } else console.error("Expected action.payload to contain new link")
        state.loadingLinks = false
      })
      .addCase(updateContentPost.pending, (state) => {
        state.loadingPosts = true
      })
      .addCase(updateContentPost.rejected, (state) => {
        state.loadingPosts = false
      })
      .addCase(updateContentPost.fulfilled, (state, action) => {
        const post = action.payload
        if (post) {
          const postIdx = state.posts.findIndex((p) => p.contentPostId === post.contentPostId)
          if (postIdx > -1) {
            state.posts[postIdx] = post
          }
        } else console.error("Expected action.payload to contain updated post")

        state.loadingPosts = false
      })
  },
})

export const CONTENT_PAGE_ACTIONS = {
  ...contentPageSlice.actions,
  fetchContentLinks,
  fetchContentPosts,
  deleteContentPost,
  deleteContentLink,
  addContentPost,
  addContentLink,
  updateContentPost,
}

export default contentPageSlice.reducer
